/**
 * Helper de analytics: registra eventos de uso na tabela `usage_events`.
 *
 * Design:
 *   - Buffer interno (max 10 eventos OU flush a cada 30s)
 *   - Fire-and-forget (nunca bloqueia UI; falha silenciosa)
 *   - Auto-detecta platform e user_role (setados via initAnalytics)
 *   - session_id gerado por sessao (renova no login)
 *
 * Uso:
 *   import { trackEvent, initAnalytics } from '@/lib/analytics'
 *
 *   // Apos login, no useAuth:
 *   initAnalytics(profile.id, profile.role)
 *
 *   // Em qualquer ponto do app:
 *   trackEvent('workout_started', { planId, exerciseCount })
 */
import { Platform } from 'react-native';
import { supabase } from './supabase';

interface AnalyticsEvent {
  user_id: string | null;
  user_role: string | null;
  event_name: string;
  properties: Record<string, unknown>;
  session_id: string;
  platform: string;
  created_at: string;
}

interface AnalyticsState {
  userId: string | null;
  userRole: string | null;
  sessionId: string;
  buffer: AnalyticsEvent[];
  flushTimer: ReturnType<typeof setTimeout> | null;
}

const FLUSH_INTERVAL_MS = 30_000;
const FLUSH_THRESHOLD = 10;

const state: AnalyticsState = {
  userId: null,
  userRole: null,
  sessionId: generateSessionId(),
  buffer: [],
  flushTimer: null,
};

function generateSessionId(): string {
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function getPlatform(): string {
  if (Platform.OS === 'web') return 'web';
  return Platform.OS; // 'ios' | 'android'
}

/**
 * Inicializa analytics com identidade do usuario. Chame apos login.
 * Renova session_id (cada login = nova sessao).
 */
export function initAnalytics(userId: string | null, userRole: string | null): void {
  state.userId = userId;
  state.userRole = userRole;
  state.sessionId = generateSessionId();
}

/**
 * Limpa identidade. Chame no logout.
 */
export function clearAnalytics(): void {
  flush(); // garante envio do que tinha
  state.userId = null;
  state.userRole = null;
  state.sessionId = generateSessionId();
}

/**
 * Registra um evento. Nunca bloqueia. Buffer ate flush.
 *
 * @param name Nome do evento (ex: 'workout_started')
 * @param properties Detalhes do evento (sem PII; apenas IDs e contagens)
 */
export function trackEvent(name: string, properties: Record<string, unknown> = {}): void {
  try {
    const event: AnalyticsEvent = {
      user_id: state.userId,
      user_role: state.userRole,
      event_name: name,
      properties,
      session_id: state.sessionId,
      platform: getPlatform(),
      created_at: new Date().toISOString(),
    };

    state.buffer.push(event);

    if (state.buffer.length >= FLUSH_THRESHOLD) {
      flush();
    } else if (!state.flushTimer) {
      state.flushTimer = setTimeout(flush, FLUSH_INTERVAL_MS);
    }
  } catch {
    // Analytics nunca quebra o app. Silencio.
  }
}

/**
 * Envia o buffer pro Supabase. Limpa timer e buffer mesmo se der erro.
 */
async function flush(): Promise<void> {
  if (state.flushTimer) {
    clearTimeout(state.flushTimer);
    state.flushTimer = null;
  }

  if (state.buffer.length === 0) return;

  const batch = state.buffer.slice();
  state.buffer = [];

  try {
    await supabase.from('usage_events').insert(batch);
  } catch {
    // Se falhar, eventos sao descartados (nao re-enfileira pra evitar loop).
  }
}

/** Flush manual (util pra debug ou momentos criticos como logout). */
export function flushAnalytics(): Promise<void> {
  return flush();
}
